import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Investor, Contact } from "@/types/investor";
import { read, utils } from "xlsx";
import { 
  clearProcessingProgress, 
  saveProcessingProgress,
  clearAllInvestorData,
  saveToSupabaseThenLocal,
  loadFromSupabaseThenLocal
} from "@/utils/investors/investorStateUtils";
import { supabase } from "@/integrations/supabase/client";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";
import { useLoading } from "@/contexts/LoadingContext";

interface FileUploaderProps {
  onFileData: (investors: Investor[], contacts: Contact[], shouldMerge?: boolean) => void;
  onReprocess: () => void;
}

const FileUploader = ({ 
  onFileData, 
  onReprocess 
}: FileUploaderProps) => {
  const [fileName, setFileName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [mergeData, setMergeData] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { supabaseSession } = useGoogleAuth();
  const { setFileUploading } = useLoading();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    
    const file = e.target.files[0];
    setFileName(file.name);
    
    try {
      setIsLoading(true);
      setFileUploading(true, `Processing ${file.name}...`, 10);
      
      const data = await file.arrayBuffer();
      setFileUploading(true, "Reading file data...", 30);
      
      const workbook = read(data);
      
      // Check if this is the slimmed-down format (single sheet with specific headers)
      if (workbook.SheetNames.length === 1) {
        setFileUploading(true, "Processing slimmed-down format...", 50);
        
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = utils.sheet_to_json<any>(sheet);
        
        // Check if this matches our slimmed down format
        if (rawData.length > 0 && 
            rawData[0].hasOwnProperty('Record') && 
            rawData[0].hasOwnProperty('Parent Record > Company') && 
            rawData[0].hasOwnProperty('Parent Record > Email addresses')) {
          
          setFileUploading(true, "Processing contacts and investors...", 70);
          
          // Process the slimmed down format
          const contacts = processSlimmedDownContacts(rawData);
          const investors = processSlimmedDownInvestors(rawData);
          
          setFileUploading(true, "Saving to database...", 90);
          
          // Database-first approach
          if (supabaseSession?.user?.id) {
            await saveToSupabaseThenLocal(
              investors, 
              contacts, 
              new Set(), 
              supabaseSession.user.id
            );
            toast.success(`Saved ${investors.length} investors and ${contacts.length} contacts to your account`);
          }
          
          setFileUploading(true, "Finalizing...", 100);
          
          // Pass processed data to parent component
          onFileData(investors, contacts, mergeData);
          
          const action = mergeData ? "Merged" : "Imported";
          toast.success(`${action} ${investors.length} investors and ${contacts.length} contacts from slimmed-down format`);
          setFileUploading(false);
          setIsLoading(false);
          return;
        }
      }
      
      setFileUploading(true, "Processing standard format...", 50);
      
      // If we get here, process as the original Excel format with 2 sheets
      if (workbook.SheetNames.length < 2) {
        toast.error("Standard format requires 2 sheets: investors and contacts");
        setFileUploading(false);
        setIsLoading(false);
        return;
      }
      
      setFileUploading(true, "Processing investors sheet...", 60);
      
      // Process investors sheet (first sheet)
      const investorsSheet = workbook.Sheets[workbook.SheetNames[0]];
      const investorsData = utils.sheet_to_json<any>(investorsSheet);
      const investors = processInvestorsData(investorsData);
      
      setFileUploading(true, "Processing contacts sheet...", 70);
      
      // Process contacts sheet (second sheet)
      const contactsSheet = workbook.Sheets[workbook.SheetNames[1]];
      const contactsData = utils.sheet_to_json<any>(contactsSheet);
      const contacts = processContactsData(contactsData);
      
      setFileUploading(true, "Saving to database...", 90);
      
      // Database-first approach
      if (supabaseSession?.user?.id) {
        await saveToSupabaseThenLocal(
          investors, 
          contacts, 
          new Set(), 
          supabaseSession.user.id
        );
        toast.success(`Saved ${investors.length} investors and ${contacts.length} contacts to your account`);
      }
      
      setFileUploading(true, "Finalizing...", 100);
      
      // Pass processed data to parent component with merge flag
      onFileData(investors, contacts, mergeData);
      
      const action = mergeData ? "Merged" : "Imported";
      toast.success(`${action} ${investors.length} investors and ${contacts.length} contacts`);
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Failed to process file. Please check format.");
    } finally {
      setFileUploading(false);
      setIsLoading(false);
    }
  };

  // Process slimmed down contact format
  const processSlimmedDownContacts = (rawData: any[]): Contact[] => {
    return rawData.map((row, index) => {
      const id = `contact-${index}`;
      const name = row["Record"] || "";
      const email = row["Parent Record > Email addresses"] || "";
      const firmName = row["Parent Record > Company"] || "";
      
      return {
        id,
        name,
        email,
        firmName,
        role: "", // These fields are nulled out in the slimmed-down format
        linkedin: "",
        twitter: "",
        notes: "",
        phone: "",
        location: ""
      };
    });
  };
  
  // Process slimmed down investor format
  const processSlimmedDownInvestors = (rawData: any[]): Investor[] => {
    // Create a map to group contacts by firm name
    const firmMap = new Map<string, any[]>();
    
    rawData.forEach(row => {
      const firmName = row["Parent Record > Company"] || "";
      if (firmName) {
        if (!firmMap.has(firmName)) {
          firmMap.set(firmName, []);
        }
        firmMap.get(firmName)?.push(row);
      }
    });
    
    // Convert firm map to investors
    const investors: Investor[] = [];
    let index = 0;
    
    firmMap.forEach((contacts, firmName) => {
      const website = contacts[0]["Parent Record > Company > Domains"] || "";
      
      investors.push({
        id: `inv-${index++}`,
        name: firmName, // Using firm name as investor name for slimmed-down format
        firm: firmName,
        // Explicitly set relevance score to 0, ignoring any column data from Excel
        relevanceScore: 0,
        website,
        keywords: [],
        portfolioCompanies: [],
        contactCount: contacts.length,
        matchedContactIds: contacts.map((_, i) => `contact-${i}`),
        location: {
          city: "",
          state: ""
        }
      });
    });
    
    return investors;
  };

  // Process raw investor data from Excel
  const processInvestorsData = (rawData: any[]): Investor[] => {
    return rawData.map((row, index) => {
      const id = row.id || `inv-${index}`;
      return {
        id,
        name: row["Investor Name"] || row["Partner Name"] || row["Full Name"] || "",
        firm: row["Fund Name"] || row["Firm Name"] || "",
        // Always set relevanceScore to 0, ignoring any value from the "Keyword Ranking" or "Relevance" columns
        relevanceScore: 0,
        keywords: row["Fund Themes (Industry)"]?.split(",").map((k: string) => k.trim()) || 
                 row["Fund Themes (Vertical)"]?.split(",").map((k: string) => k.trim()) || 
                 row["Keywords"]?.split(",").map((k: string) => k.trim()) || [],
        portfolioCompanies: row["Relevant Portfolio Companies"]?.split(",").map((c: string) => c.trim()) || 
                           row["Portfolio"]?.split(",").map((c: string) => c.trim()) || [],
        investmentFocus: row["Investment Preference (Lead in Green)"] || row["Focus"] || row["Investment Focus"] || "",
        website: row["Website"] || "",
        notes: row["Additional Notes"] || row["Why would they be interested?"] || row["Notes"] || "",
        location: {
          city: row["Location (City)"] || "",
          state: row["Location (State)"] || "",
        },
      };
    });
  };
  
  // Process raw contact data from Excel with the new column headers
  const processContactsData = (rawData: any[]): Contact[] => {
    return rawData.map((row, index) => {
      const id = row.id || `contact-${index}`;
      // Create full name from first and last name if available
      let fullName = row["Full Name"] || "";
      if (!fullName && row["First Name"] && row["Last Name"]) {
        fullName = `${row["First Name"]} ${row["Last Name"]}`;
      }
      
      return {
        id,
        name: fullName || row["Investor Name"] || row["Contact Name"] || row["Name"] || "",
        role: row["Primary Position"] || row["Investor Title"] || row["Role"] || row["Position"] || row["Job Title"] || "",
        firmName: row["Fund Name"] || row["Firm Name"] || "",
        email: row["Email"] || row["Investor Email"] || row["Email Address"] || "",
        linkedin: row["LinkedIn URL"] || row["LinkedIn"] || "",
        twitter: row["Twitter"] || row["Twitter URL"] || "",
        notes: row["Biography"] || row["Additional Notes"] || row["Notes"] || "",
        phone: row["Phone"] || row["Phone Number"] || "",
        location: row["Location"] || 
                 (row["Location (City)"] ? 
                  (row["Location (State)"] ? 
                   `${row["Location (City)"]}, ${row["Location (State)"]}` : 
                   row["Location (City)"]) : 
                  row["Address"] || ""),
      };
    });
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleReprocessClick = () => {
    onReprocess();
  };

  const handleClearData = async () => {
    try {
      setIsClearing(true);
      
      // Use the centralized clearAllInvestorData function
      await clearAllInvestorData(supabaseSession?.user?.id);
      
      // Only preserve specific localStorage items that should be kept
      const itemsToPreserve = [
        "openai_token",
        "perplexity_token",
        "openai_api_key",
        "perplexity_api_key"
      ];
      
      // Get all storage keys
      const allKeys = Object.keys(localStorage);
      
      // Remove everything except preserved items
      allKeys.forEach(key => {
        if (!itemsToPreserve.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear filename
      setFileName("");
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      toast.success("All data cleared successfully");
    } catch (error) {
      console.error("Error clearing data:", error);
      toast.error("Failed to clear data completely");
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
        <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center rounded-full bg-blue-50 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        
        <h3 className="text-lg font-medium text-gray-900 mb-1">Upload Excel or CSV File</h3>
        <p className="text-sm text-gray-500 mb-4">
          Drag and drop or click to upload your investor data file
        </p>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          className="hidden"
        />
        
        <div className="flex flex-row justify-center space-x-2 mb-4">
          <Button 
            onClick={handleButtonClick}
            disabled={isLoading || isClearing}
            variant="outline"
            className="w-32"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Select File
          </Button>
          
          <Button
            onClick={handleReprocessClick}
            disabled={isClearing}
            variant="outline"
            className="w-32"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reprocess Data
          </Button>
          
          <Button
            onClick={handleClearData}
            disabled={isClearing}
            variant="destructive"
            className="w-24"
          >
            {isClearing ? "Clearing..." : "Clear Data"}
          </Button>
        </div>
        
        <div className="flex items-center justify-center space-x-2 mb-2">
          <Checkbox 
            id="merge-data" 
            checked={mergeData} 
            onCheckedChange={(checked) => setMergeData(checked as boolean)} 
          />
          <label 
            htmlFor="merge-data" 
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Merge with existing data (preserve enriched information)
          </label>
        </div>
        
        {fileName && (
          <p className="mt-2 text-sm text-gray-600">
            File: {fileName}
          </p>
        )}
      </div>

      <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-2">Supported File Formats</h4>
        <ul className="list-disc pl-5 space-y-1 mb-2">
          <li><span className="font-medium">Standard format:</span> Excel file with two sheets (investors & contacts)</li>
          <li><span className="font-medium">Slimmed-down format:</span> Single sheet with specific headers</li>
        </ul>
        <p className="text-xs">For slimmed-down format, include columns: <code>Record</code>, <code>Parent Record &gt; Company</code>, <code>Parent Record &gt; Company &gt; Domains</code>, <code>Parent Record &gt; Email addresses</code></p>
      </div>
    </div>
  );
};

export default FileUploader;
